import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { Input, Collapse } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { RootState } from '../../store';
import TreeNode from '../TreeNode/TreeNode';
import { fetchArtifacts, checkRootStatus, fetchArtifact, deleteArtifact } from '../../store/slices/ArtifactSlice';
import styles from './ArtifactTree.module.css';
import { useArtifactTree } from '../../contexts';
import { useTreeState } from '../../hooks/useTreeState';
import {
  EnhancedArtifact,
  type ArtifactTree as ArtifactTreeType,
} from '../../types/artifacts';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useSelector } from 'react-redux';
import {
  fetchBusinessModels,
  setCurrentModel,
  createBusinessModel,
} from '../../store/slices/BusinessModelSlice';
import BusinessModelSelector from '../BusinessModel/BusinessModelSelector';
import { createArtifact } from '../../store/slices/ArtifactSlice';
import { fetchArtifactTypes } from '../../store/slices/artifactTypeSlice';
import { ArtifactTabs } from './ArtifactTabs';
import { BusinessModel, BusinessModelCreate } from '../../types/businessModel';
import { CArtifact } from '../../types/artifacts-branded';
import { PrimeToCompanyImportDialog } from './PrimeToCompanyImportDialog';
import { NodePrimeImportDialog } from './NodePrimeImportDialog';
import { useUser } from '../../contexts/UserContext';
import { ArtifactStateFlowDiagram } from '../PrimeModel/ArtifactStateFlowDiagram';

const { Search } = Input;

export const ArtifactTree: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedCompany } = useUser();
  const { currentArtifact } = useAppSelector((state: RootState) => state.artifacts);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Debug logging for current artifact - only sample in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
      console.log('[ArtifactTree] Current artifact changed (sampled):', currentArtifact?.id);
    }
  }, [currentArtifact, selectedId]);

  const { items: artifactTypes } = useAppSelector((state: RootState) => state.artifactTypes);
  const [usePrimeForm, setUsePrimeForm] = useState(false); // Toggle for prime form
  const [hasUserSetPrimeForm, setHasUserSetPrimeForm] = useState(false); // Track if user manually set the toggle
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isNodeImportDialogOpen, setIsNodeImportDialogOpen] = useState(false);
  const [selectedArtifactForImport, setSelectedArtifactForImport] = useState<EnhancedArtifact | null>(null);
  const [artifactSearchTerm, setArtifactSearchTerm] = useState('');
  const [diagramCollapsed, setDiagramCollapsed] = useState(() => {
    const saved = localStorage.getItem('companyArtifactDiagramCollapsed');
    return saved ? saved === 'true' : true; // Default to collapsed
  });
  const [statesForDiagram, setStatesForDiagram] = useState<any[]>([]);
  const [transitionsForDiagram, setTransitionsForDiagram] = useState<any[]>([]);
  
  // Tree section width state (resizable)
  const [treeSectionWidth, setTreeSectionWidth] = useState(() => {
    const saved = localStorage.getItem('companyArtifactTreeSectionWidth');
    return saved ? parseInt(saved, 10) : 450; // Default to 450px
  });
  const [isResizing, setIsResizing] = useState(false);

  // Auto-detect if artifact is connected to prime artifact (only if user hasn't manually set it)
  useEffect(() => {
    if (currentArtifact && currentArtifact.prime_artifact_id && !hasUserSetPrimeForm) {
      // If artifact has a prime artifact connection, suggest using enhanced form
      setUsePrimeForm(true);
    }
  }, [currentArtifact, hasUserSetPrimeForm]);

  const { hierarchy, loading, error } = useAppSelector((state: RootState) => state.artifacts);
  const rootStatus = useAppSelector((state: RootState) => state.artifacts.rootStatus);

  // Debug logging for hierarchy - only sample in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
      console.log('[ArtifactTree] Hierarchy length (sampled):', hierarchy.length);
    }
  }, [hierarchy]);

  // Add null check for context
  let contextSelectedTree: ArtifactTreeType | null = null;
  try {
    const context = useArtifactTree();
    contextSelectedTree = context?.selectedTree || null;
  } catch (contextError) {
    console.error('[ArtifactTree] Error accessing artifact tree context:', contextError);
    contextSelectedTree = null;
  }

  const { handleSelect, handleToggleExpand, isExpanded } = useTreeState<
    EnhancedArtifact,
    string
  >((id: string) => {
    try {
      // Only log in development mode and sample to reduce verbosity
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('[ArtifactTree] Tree selection changed (sampled):', id);
      }

      // Always fetch the artifact to set it as currentArtifact in Redux
      // This ensures currentArtifact is properly set for the ArtifactTabs component
      void dispatch(fetchArtifact(id));

      // Update the local selectedId state when tree selection changes
      setSelectedId(id);
    } catch (error) {
      console.error('[ArtifactTree] Error in handleSelect callback:', error);
    }
  });

  // Create a synchronous wrapper for handleSelect
  const handleSelectSync = useCallback(
    (id: string) => {
      void handleSelect(id, hierarchy);
    },
    [handleSelect, hierarchy]
  );

  const { models, currentModel } = useSelector((state: RootState) => state.businessModels);

  // Use refs to track previous values and prevent unnecessary re-fetches
  const prevTreeIdRef = useRef<string | null>(null);
  const prevModelIdRef = useRef<string | null>(null);
  const prevCompanyIdRef = useRef<string | undefined>(undefined);
  const prevSelectedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (contextSelectedTree) {
        const treeId = contextSelectedTree.id;
        const companyId = selectedCompany?.id || undefined;

        // Skip if we already loaded data for this tree and company
        if (prevTreeIdRef.current === treeId && prevCompanyIdRef.current === companyId) {
          return;
        }

        try {
          await Promise.all([
            dispatch(fetchArtifacts({ treeId, companyId })),
            dispatch(checkRootStatus({ treeId, companyId })),
            dispatch(fetchBusinessModels(treeId)),
          ]);
          prevTreeIdRef.current = treeId;
          prevCompanyIdRef.current = companyId;
        } catch (error) {
          console.error('Failed to load initial data:', error);
        }
      }
    };
    void loadData();
  }, [dispatch, contextSelectedTree, contextSelectedTree?.id, selectedCompany?.id]);

  useEffect(() => {
    const loadArtifact = async () => {
      if (selectedId && selectedId !== prevSelectedIdRef.current) {
        try {
          await dispatch(fetchArtifact(selectedId));
          prevSelectedIdRef.current = selectedId;
        } catch (error) {
          console.error('Failed to load artifact:', error);
        }
      }
    };
    void loadArtifact();
  }, [selectedId, dispatch]);

  useEffect(() => {
    const loadArtifactsForModel = async () => {
      if (contextSelectedTree && currentModel) {
        const treeId = contextSelectedTree.id;
        const modelId = currentModel.id;
        const companyId = selectedCompany?.id || undefined;
        
        // Only fetch if model changed (not on initial load when we already fetched in loadData)
        if (prevModelIdRef.current === modelId && 
            prevTreeIdRef.current === treeId && 
            prevCompanyIdRef.current === companyId) {
          return;
        }

        try {
          await dispatch(
            fetchArtifacts({
              treeId,
              businessModelId: modelId,
              companyId,
            })
          );
          prevModelIdRef.current = modelId;
          prevTreeIdRef.current = treeId;
          prevCompanyIdRef.current = companyId;
        } catch (error) {
          console.error('Failed to load artifacts for model:', error);
        }
      }
    };
    void loadArtifactsForModel();
  }, [dispatch, contextSelectedTree, contextSelectedTree?.id, currentModel, currentModel?.id, selectedCompany?.id]);

  // Tree section resize handlers
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, []);

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    const minWidth = 300;
    const maxWidth = 800;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setTreeSectionWidth(newWidth);
    }
  }, [isResizing]);

  const handleResizeMouseUp = useCallback(() => {
    if (isResizing) {
      localStorage.setItem('companyArtifactTreeSectionWidth', treeSectionWidth.toString());
      setIsResizing(false);
    }
  }, [isResizing, treeSectionWidth]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMouseMove, handleResizeMouseUp]);

  // Memoize the add child handler
  const handleAddChild = useCallback((parentId: string) => {
    setSelectedId(`new-${parentId}`);
  }, []);

  // Memoize the delete handler
  const handleDelete = useCallback(async (id: string) => {
    try {
      // Find the artifact to get its name for confirmation
      const artifactToDelete = hierarchy.find((a) => a.id === id);
      const artifactName = artifactToDelete?.name || 'this artifact';

      // Confirm deletion
      const confirmed = window.confirm(
        `Are you sure you want to delete "${artifactName}"?\n\nThis action cannot be undone.`
      );

      if (!confirmed) {
        return;
      }

      console.log('Delete artifact:', id);
      
      // Dispatch the delete action
      await dispatch(deleteArtifact(id)).unwrap();
      
      // Clear selection if the deleted artifact was selected
      if (selectedId === id) {
        setSelectedId(null);
      }
      
      // Refresh the artifact tree
      if (contextSelectedTree?.id && selectedCompany?.id) {
        await dispatch(
          fetchArtifacts({
            treeId: contextSelectedTree.id,
            companyId: selectedCompany.id,
          })
        );
      }
    } catch (error) {
      console.error('Failed to delete artifact:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete artifact';
      alert(`Error deleting artifact: ${errorMessage}`);
    }
  }, [dispatch, hierarchy, selectedId, contextSelectedTree?.id, selectedCompany?.id]);

  // Handle import from prime for a specific node
  const handleImportFromPrime = useCallback((id: string) => {
    // Find the artifact in the hierarchy
    const findArtifact = (artifacts: EnhancedArtifact[], targetId: string): EnhancedArtifact | null => {
      for (const artifact of artifacts) {
        if (artifact.id === targetId) {
          return artifact;
        }
        if (artifact.children) {
          const found = findArtifact(artifact.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const artifact = findArtifact(hierarchy, id);
    if (artifact) {
      if (!artifact.prime_artifact_id) {
        alert('This artifact does not have a linked prime artifact. Cannot import children or peers.');
        return;
      }
      setSelectedArtifactForImport(artifact);
      setIsNodeImportDialogOpen(true);
    } else {
      console.error('[ArtifactTree] Artifact not found for import:', id);
    }
  }, [hierarchy]);

  const handleNodeImportComplete = useCallback(async () => {
    // Refresh the artifact tree after import
    if (contextSelectedTree?.id && selectedCompany?.id) {
      await dispatch(
        fetchArtifacts({
          treeId: contextSelectedTree.id,
          companyId: selectedCompany.id,
        })
      );
    }
  }, [dispatch, contextSelectedTree?.id, selectedCompany?.id]);

  const handleModelSelect = useCallback(
    (model: BusinessModel | null) => {
      if (model) {
        void dispatch(setCurrentModel(model));
        // Reset selected artifact when changing business model
        setSelectedId(null);
      }
    },
    [dispatch]
  );

  const handleCreateModel = useCallback(
    async (model: BusinessModelCreate & { creationType?: string }) => {
      try {
        console.log('[ArtifactTree] Creating business model with data:', model);
        
        // Extract only the fields the backend expects for business model creation
        const businessModelData: BusinessModelCreate = {
          artifact_tree_id: model.artifact_tree_id,
          name: model.name,
          ...(model.description ? { description: model.description } : {}),
          ...(model.copy_from_model_id ? { copy_from_model_id: model.copy_from_model_id } : {}),
        };
        
        console.log('[ArtifactTree] Sending to backend:', businessModelData);
        const result = await dispatch(createBusinessModel(businessModelData)).unwrap();
        console.log('[ArtifactTree] Business model created successfully:', result);
        
        void dispatch(setCurrentModel(result));
        
        // Refresh business models list
        if (contextSelectedTree?.id) {
          await dispatch(fetchBusinessModels(contextSelectedTree.id));
        }
        
        console.log('[ArtifactTree] Business model creation completed successfully');
      } catch (error: any) {
        console.error('[ArtifactTree] Failed to create business model:', error);
        const errorMessage = error?.response?.data?.detail || 
                           error?.response?.data?.message || 
                           error?.message || 
                           'Failed to create business model';
        alert(`Error creating business model: ${errorMessage}`);
        throw error; // Re-throw to prevent modal from closing
      }
    },
    [dispatch, contextSelectedTree?.id, selectedCompany?.id]
  );

  // Simple artifact creation form for when there are no artifacts
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newArtifactData, setNewArtifactData] = useState({
    name: '',
    description: '',
    artifact_type_id: '',
  });
  const [loadingArtifactTypes, setLoadingArtifactTypes] = useState(false);
  const [artifactTypeError, setArtifactTypeError] = useState<string | null>(null);

  // Fetch artifact types when the form is shown
  useEffect(() => {
    const loadArtifactTypes = async () => {
      if (showCreateForm && artifactTypes.length === 0) {
        console.log('[ArtifactTree] Loading artifact types...');
        setLoadingArtifactTypes(true);
        setArtifactTypeError(null);
        try {
          const result = await dispatch(fetchArtifactTypes()).unwrap();
          console.log('[ArtifactTree] Artifact types loaded:', result);
          // Artifact types are now managed through Redux state
        } catch (error) {
          console.error('[ArtifactTree] Failed to fetch artifact types:', error);
          setArtifactTypeError('Failed to load artifact types. Please try again.');
        } finally {
          setLoadingArtifactTypes(false);
        }
      }
    };

    void loadArtifactTypes();
  }, [showCreateForm, artifactTypes.length, dispatch]);

  const handleCreateFirstArtifact = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newArtifactData.name.trim() || !newArtifactData.artifact_type_id) {
        return;
      }

      try {
        const companyIdToUse = selectedCompany?.id;
        
        if (!companyIdToUse) {
          throw new Error('Company assignment required. Please select a company from the user menu (top right) or go to User-company links to assign yourself to a company.');
        }
        
        console.log('[ArtifactTree] Creating artifact with company_id:', companyIdToUse);
        
        // Find the artifact type name from the ID
        const selectedArtifactType = artifactTypes.find(
          (type) => type.id === newArtifactData.artifact_type_id
        );
        const artifactTypeName = selectedArtifactType?.name;
        
        await dispatch(
          createArtifact({
            name: newArtifactData.name.trim(),
            description: newArtifactData.description.trim() || undefined,
            artifact_type_id: newArtifactData.artifact_type_id,
            artifact_type: artifactTypeName, // Include artifact type name as backend may require it
            artifact_tree_id: contextSelectedTree?.id || undefined,
            parent_ref: undefined,
            company_id: companyIdToUse,
          })
        ).unwrap();

        // Refresh root status and artifacts to reflect the new root artifact
        if (contextSelectedTree?.id) {
          await Promise.all([
            dispatch(checkRootStatus({ treeId: contextSelectedTree.id, companyId: selectedCompany?.id })),
            dispatch(fetchArtifacts({ treeId: contextSelectedTree.id, companyId: selectedCompany?.id })),
          ]);
        }

        // Reset form
        setNewArtifactData({ name: '', description: '', artifact_type_id: '' });
        setShowCreateForm(false);
      } catch (error: any) {
        console.error('[ArtifactTree] Failed to create artifact:', error);
        
        // Extract error message from various possible formats
        let errorMessage = 'Failed to create artifact. Please try again.';
        
        try {
          if (error?.response?.data) {
            const errorData = error.response.data;
            const detail = errorData.detail;
            
            if (Array.isArray(detail)) {
              // Validation errors array
              const errorMessages = detail.map((err: any) => {
                const field = err.loc?.join('.') || 'unknown field';
                const msg = err.msg || err.message || 'Validation error';
                return `${field}: ${msg}`;
              });
              errorMessage = errorMessages.join('\n');
            } else if (typeof detail === 'string') {
              errorMessage = detail;
            } else if (detail && typeof detail === 'object') {
              errorMessage = JSON.stringify(detail, null, 2);
            }
          } else if (error?.message) {
            errorMessage = String(error.message);
          } else if (typeof error === 'string') {
            errorMessage = error;
          } else if (error && typeof error === 'object') {
            // Fallback: try to stringify the error object
            try {
              errorMessage = JSON.stringify(error, null, 2);
            } catch {
              errorMessage = 'An unknown error occurred';
            }
          }
        } catch (parseError) {
          // If error parsing fails, use a safe default message
          errorMessage = 'Failed to create artifact. Please try again.';
        }
        
        // Ensure errorMessage is always a string
        if (typeof errorMessage !== 'string') {
          errorMessage = String(errorMessage);
        }
        
        // Show user-friendly error message
        if (errorMessage.includes('company_id') || errorMessage.includes('company')) {
          alert('Company assignment required. Please select a company from the user menu (top right) or go to User-company links to assign yourself to a company.');
        } else {
          alert(`Error creating artifact:\n${errorMessage}`);
        }
      }
    },
    [dispatch, newArtifactData, contextSelectedTree?.id, selectedCompany?.id, artifactTypes]
  );

  // Filter hierarchy by search term
  const filterHierarchy = useCallback((artifacts: EnhancedArtifact[], searchTerm: string): EnhancedArtifact[] => {
    if (!searchTerm) return artifacts;
    
    const lowercaseSearch = searchTerm.toLowerCase();
    
    const filtered: EnhancedArtifact[] = [];
    
    for (const artifact of artifacts) {
      const matches = artifact.name.toLowerCase().includes(lowercaseSearch);
      const filteredChildren = artifact.children 
        ? filterHierarchy(artifact.children, searchTerm)
        : [];
      
      if (matches || filteredChildren.length > 0) {
        filtered.push({
          ...artifact,
          children: filteredChildren.length > 0 ? filteredChildren : artifact.children,
        });
      }
    }
    
    return filtered;
  }, []);

  const filteredHierarchy = useMemo(() => {
    return filterHierarchy(hierarchy, artifactSearchTerm);
  }, [hierarchy, artifactSearchTerm, filterHierarchy]);

  // Handle diagram collapse toggle
  const handleDiagramCollapseToggle = useCallback(() => {
    const newCollapsed = !diagramCollapsed;
    setDiagramCollapsed(newCollapsed);
    localStorage.setItem('companyArtifactDiagramCollapsed', newCollapsed.toString());
  }, [diagramCollapsed]);

  // Load diagram data for selected artifact
  useEffect(() => {
    const loadDiagramData = async () => {
      if (!currentArtifact?.id) {
        setStatesForDiagram([]);
        setTransitionsForDiagram([]);
        return;
      }

      try {
        // For company artifacts, we may need to load states/transitions differently
        // This is a placeholder - actual implementation depends on company artifact API
        // For now, we'll leave it empty since company artifacts may not have all fields
        setStatesForDiagram([]);
        setTransitionsForDiagram([]);
      } catch (error) {
        console.error('[ArtifactTree] Error loading diagram data:', error);
      }
    };

    void loadDiagramData();
  }, [currentArtifact?.id]);

  // Render tree content
  const renderTreeContent = useMemo(() => {
    if (!rootStatus?.hasRoot) {
      // Show direct artifact creation instead of NoRootPrompt
      return (
        <div className={styles.noArtifactsPrompt}>
          <h3>Create Your First Artifact</h3>
          <p>Start building your artifact tree by creating your first artifact:</p>

          {!showCreateForm ? (
            <div className={styles.createFirstArtifact}>
              <button className={styles.createArtifactButton} onClick={() => setShowCreateForm(true)}>
                Create First Artifact
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreateFirstArtifact} className={styles.createArtifactForm}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={newArtifactData.name}
                  onChange={(e) =>
                    setNewArtifactData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={newArtifactData.description}
                  onChange={(e) =>
                    setNewArtifactData((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>

              <div className="form-group">
                <label htmlFor="artifact_type_id">Artifact Type *</label>
                <select
                  id="artifact_type_id"
                  value={newArtifactData.artifact_type_id}
                  onChange={(e) =>
                    setNewArtifactData((prev) => ({ ...prev, artifact_type_id: e.target.value }))
                  }
                  required
                >
                  <option value="">Select an artifact type</option>
                  {loadingArtifactTypes ? (
                    <option value="">Loading artifact types...</option>
                  ) : (
                    artifactTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))
                  )}
                </select>
                {artifactTypeError && <div className={styles.errorMessage}>{artifactTypeError}</div>}
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
                <button type="submit">Create Artifact</button>
              </div>
            </form>
          )}
        </div>
      );
    }

    if (loading && hierarchy.length === 0) {
      return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
      return <div className={styles.error}>{error}</div>;
    }

    return (
      <div className={styles.artifactTree}>
        <div className={styles.treeContainer}>
          <div className={styles.treeHeader}>
            <h3>Company Artifacts</h3>
          </div>
          <Search
            placeholder="Search artifacts..."
            value={artifactSearchTerm}
            onChange={(e) => setArtifactSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            style={{ marginBottom: '12px' }}
          />
          <div className={styles.treeView}>
            {filteredHierarchy.map((node: EnhancedArtifact) => (
              <TreeNode<EnhancedArtifact>
                key={node.id}
                node={node}
                onSelect={handleSelectSync}
                selectedId={selectedId}
                onAddChild={handleAddChild}
                onDelete={handleDelete}
                onImportFromPrime={handleImportFromPrime}
                onToggleExpand={handleToggleExpand}
                isExpanded={isExpanded}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }, [
    rootStatus?.hasRoot,
    loading,
    error,
    hierarchy,
    handleSelectSync,
    selectedId,
    handleAddChild,
    handleDelete,
    handleToggleExpand,
    isExpanded,
    showCreateForm,
    newArtifactData,
    handleCreateFirstArtifact,
    artifactTypes,
    loadingArtifactTypes,
    artifactTypeError,
    filteredHierarchy,
    artifactSearchTerm,
  ]);

  try {
    return (
      <div className={styles.artifactTreeWrapper}>
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <BusinessModelSelector
              models={models}
              currentModel={currentModel}
              onModelSelect={handleModelSelect}
              onCreateModel={handleCreateModel}
              artifactTreeId={contextSelectedTree?.id || ''}
            />
            <button
              onClick={() => setIsImportDialogOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Import from Prime Model
            </button>
          </div>
        </div>
        <div className={styles.artifactTreeContainer}>
          <div 
            className={`${styles.treeSection} ${isResizing ? styles.resizing : ''}`}
            style={{ width: `${treeSectionWidth}px`, minWidth: `${treeSectionWidth}px`, maxWidth: `${treeSectionWidth}px` }}
          >
            {renderTreeContent}
            <div
              className={styles.treeResizeHandle}
              onMouseDown={handleResizeMouseDown}
              title="Drag to resize tree section"
            />
            {currentArtifact && (
              <Collapse
                ghost
                activeKey={diagramCollapsed ? [] : ['diagram']}
                onChange={(keys) => {
                  if (keys.length === 0) {
                    setDiagramCollapsed(true);
                    localStorage.setItem('companyArtifactDiagramCollapsed', 'true');
                  } else {
                    setDiagramCollapsed(false);
                    localStorage.setItem('companyArtifactDiagramCollapsed', 'false');
                  }
                }}
                items={[
                  {
                    key: 'diagram',
                    label: 'State Flow Diagram',
                    children: (
                      <ArtifactStateFlowDiagram
                        states={statesForDiagram}
                        transitions={transitionsForDiagram}
                        artifactName={currentArtifact.name}
                        artifactId={currentArtifact.id}
                        collapsed={diagramCollapsed}
                        onCollapseToggle={handleDiagramCollapseToggle}
                      />
                    ),
                  },
                ]}
              />
            )}
          </div>
          {selectedId && (
            <div className={styles.detailsPanel}>
              {(() => {
                const foundArtifact = hierarchy.find((artifact) => artifact.id === selectedId);
                const artifactToPass = currentArtifact
                  ? (currentArtifact as unknown as CArtifact)
                  : foundArtifact
                    ? (foundArtifact as unknown as CArtifact)
                    : null;

                return (
                  <ArtifactTabs
                    usePrime={usePrimeForm}
                    onTogglePrime={(value) => {
                      setUsePrimeForm(value);
                      setHasUserSetPrimeForm(true);
                    }}
                    artifact={artifactToPass}
                  />
                );
              })()}
            </div>
          )}
        </div>

        <PrimeToCompanyImportDialog
          isOpen={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onImportComplete={() => {
            setIsImportDialogOpen(false);
            // Refresh the artifact tree
            void dispatch(fetchArtifacts({ treeId: contextSelectedTree?.id || '', companyId: selectedCompany?.id }));
          }}
          companyArtifacts={hierarchy}
          selectedCompanyArtifact={currentArtifact}
          companyId={selectedCompany?.id}
        />
        {selectedArtifactForImport && (
          <NodePrimeImportDialog
            isOpen={isNodeImportDialogOpen}
            onClose={() => {
              setIsNodeImportDialogOpen(false);
              setSelectedArtifactForImport(null);
            }}
            onImportComplete={handleNodeImportComplete}
            companyArtifact={selectedArtifactForImport}
            companyId={selectedCompany?.id}
            companyArtifacts={hierarchy}
          />
        )}
      </div>
    );
  } catch (renderError) {
    console.error('[ArtifactTree] Error rendering component:', renderError);
    return (
      <div className="p-4">
        <div className={styles.error}>
          <h2>Error Loading Artifact Tree</h2>
          <p>There was an error loading the artifact tree. Please refresh the page.</p>
          <details>
            <summary>Error Details</summary>
            <pre>{renderError instanceof Error ? renderError.message : String(renderError)}</pre>
          </details>
        </div>
      </div>
    );
  }
};

export default ArtifactTree;
